import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Nav from "../Components/Nav";
import { auth, db } from "../config";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { onAuthStateChanged, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import axios from "axios";
import Footer from "../Components/Footer";

function Settings() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("per");
  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
    phone: "",
    dob: "",
    address: "",
  });
  const [withdrawalForm, setWithdrawalForm] = useState({
    btc_address: "",
    eth_address: "",
    ltc_address: "",
    usdt_address: "",
    tron_address: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });
  const [emailPrefForm, setEmailPrefForm] = useState({
    otpsend: "Yes",
    roiemail: "Yes",
    invplanemail: "Yes",
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setProfileForm({
              name: userData.name || currentUser.displayName || "",
              email: userData.email || currentUser.email || "",
              phone: userData.phone || "",
              dob: userData.dob || "",
              address: userData.address || "",
            });
            setWithdrawalForm({
              btc_address: userData.crypto_addresses?.btc_address || "",
              eth_address: userData.crypto_addresses?.eth_address || "",
              ltc_address: userData.crypto_addresses?.ltc_address || "",
              usdt_address: userData.crypto_addresses?.usdt_address || "",
              tron_address: userData.crypto_addresses?.tron_address || "",
            });
            setEmailPrefForm({
              otpsend: userData.email_preferences?.otpsend || "Yes",
              roiemail: userData.email_preferences?.roiemail || "Yes",
              invplanemail: userData.email_preferences?.invplanemail || "Yes",
            });
          } else {
            setMessage("User data not found.");
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setMessage("Error fetching user data.");
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        navigate("/auth/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleWithdrawalChange = (e) => {
    const { name, value } = e.target;
    setWithdrawalForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmailPrefChange = (e) => {
    const { name, value } = e.target;
    setEmailPrefForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setMessage("User not authenticated.");
      return;
    }

    setSubmitting(true);
    try {
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, {
        name: profileForm.name,
        phone: profileForm.phone,
        dob: profileForm.dob,
        address: profileForm.address,
      });

      // await axios.post("/api/send-email", {
      //   name: profileForm.name,
      //   email: profileForm.email,
      //   subject: "Profile Updated - APEXYEILD",
      //   message: `Dear ${profileForm.name},\n\nYour profile has been updated successfully.\n\nUpdated Details:\nName: ${profileForm.name}\nPhone: ${profileForm.phone}\nDate of Birth: ${profileForm.dob}\nAddress: ${profileForm.address}\n\nThank you,\nAPEXYEILD Team`,
      // });

      setMessage("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      setMessage("Error updating profile: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setMessage("User not authenticated.");
      return;
    }

    setSubmitting(true);
    try {
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, {
        crypto_addresses: {
          btc_address: withdrawalForm.btc_address,
          eth_address: withdrawalForm.eth_address,
          ltc_address: withdrawalForm.ltc_address,
          usdt_address: withdrawalForm.usdt_address,
          tron_address: withdrawalForm.tron_address,
        },
      });

      // await axios.post("/api/send-email", {
      //   name: profileForm.name,
      //   email: profileForm.email,
      //   subject: "Withdrawal Settings Updated - APEXYEILD",
      //   message: `Dear ${profileForm.name},\n\nYour withdrawal settings have been updated successfully.\n\nUpdated Crypto Addresses:\nBitcoin: ${withdrawalForm.btc_address}\nEthereum: ${withdrawalForm.eth_address}\nLitecoin: ${withdrawalForm.ltc_address}\nUSDT: ${withdrawalForm.usdt_address}\nTron: ${withdrawalForm.tron_address}\n\nThank you,\nAPEXYEILD Team`,
      // });

      setMessage("Withdrawal settings updated successfully!");
    } catch (error) {
      console.error("Error updating withdrawal settings:", error);
      setMessage("Error updating withdrawal settings: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setMessage("User not authenticated.");
      return;
    }

    if (passwordForm.password !== passwordForm.password_confirmation) {
      setMessage("New password and confirmation do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, passwordForm.current_password);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordForm.password);

      // await axios.post("/api/send-email", {
      //   name: profileForm.name,
      //   email: profileForm.email,
      //   subject: "Password Updated - APEXYEILD",
      //   message: `Dear ${profileForm.name},\n\nYour password has been updated successfully.\n\nFor security, please keep your new password safe and do not share it with anyone.\n\nThank you,\nAPEXYEILD Team`,
      // });

      setMessage("Password updated successfully!");
      setPasswordForm({ current_password: "", password: "", password_confirmation: "" });
    } catch (error) {
      console.error("Error updating password:", error);
      setMessage("Error updating password: " + (error.code === "auth/wrong-password" ? "Incorrect current password." : error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailPrefSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setMessage("User not authenticated.");
      return;
    }

    setSubmitting(true);
    try {
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, {
        email_preferences: {
          otpsend: emailPrefForm.otpsend,
          roiemail: emailPrefForm.roiemail,
          invplanemail: emailPrefForm.invplanemail,
        },
      });

      // await axios.post("/api/send-email", {
      //   name: profileForm.name,
      //   email: profileForm.email,
      //   subject: "Email Preferences Updated - APEXYEILD",
      //   message: `Dear ${profileForm.name},\n\nYour email preferences have been updated successfully.\n\nUpdated Preferences:\nSend OTP on withdrawal: ${emailPrefForm.otpsend}\nSend profit emails: ${emailPrefForm.roiemail}\nSend plan expiration emails: ${emailPrefForm.invplanemail}\n\nThank you,\nAPEXYEILD Team`,
      // });

      setMessage("Email preferences updated successfully!");
    } catch (error) {
      console.error("Error updating email preferences:", error);
      setMessage("Error updating email preferences: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMessage(""); // Clear messages when switching tabs
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>APEXYEILD | Account Settings</title>
        <link
          rel="icon"
          href="https://apexyeild.com/auth/storage/app/public/photos/IQAXeGtikmark.png1744508352"
          type="image/png"
        />
      </Helmet>

      <style>
        {`
          .custom-select {
            display: inline-block;
            max-width: 100%;
            height: calc(2.25rem + 2px);
            padding: .375rem 1.75rem .375rem .75rem;
            line-height: 1.5;
            color: #0b57e3;
            vertical-align: middle;
            background: #fff url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23333' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E") no-repeat right .75rem center;
            background-size: 8px 10px;
            border: 4px solid black;
            border-radius: .25rem;
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
          }
          .custom-select:focus {
            border-color: #80bdff;
            outline: 0;
          }
          .custom-select:disabled {
            color: #868e96;
            background-color: #e9ecef;
          }
          .footer-block {
            min-height: 400px;
            padding: 50px 0;
            background: linear-gradient(to right, #212529, #131516), url(../img/project4.jpg);
          }
          .foot-header {
            font-family: "Montserrat", Sans-serif;
            font-weight: 600;
            font-size: 1rem;
            margin-bottom: 25px;
            color: #4A4A4A;
          }
          .foot {
            display: block;
            font-size: small;
            margin-bottom: 8px;
            font-family: "Montserrat", Sans-serif;
            color: #A1A39E !important;
          }
          .foot:hover {
            text-decoration: none !important;
            color: #FE413C !important;
          }
          .foot::before {
            content: "-";
            margin-right: 3px;
            color: #FE413C;
          }
          .footer-credit {
            font-family: "Montserrat", Sans-serif;
            color: #FE413C;
            font-size: small;
          }
          .footer-block .small-image {
            width: 250px;
            height: auto;
          }
          .nav-tabs {
            border-bottom: 2px solid #dee2e6;
          }
          .nav-tabs .nav-link {
            font-size: 1.25rem;
            font-weight: 500;
            color: #212529;
            background-color: #f8f9fa;
            border: none;
            border-bottom: 3px solid transparent;
            padding: 0.75rem 1.25rem;
            cursor: pointer;
            transition: all 0.3s ease;
          }
          .nav-tabs .nav-link.active {
            color: #fff;
            background-color: #007bff;
            border-bottom: 3px solid #0056b3;
          }
          .nav-tabs .nav-link:hover {
            color: #0056b3;
            border-bottom: 3px solid #0056b3;
          }
          .message {
            margin-top: 15px;
            padding: 10px;
            border-radius: 5px;
          }
          .message.success {
            background-color: #d4edda;
            color: #155724;
          }
          .message.error {
            background-color: #f8d7da;
            color: #721c24;
          }
          .sidebar {
            min-height: 100vh;
          }
          .sidebar .nav-item.active .nav-link {
            background-color: #007bff;
            color: #fff !important;
          }
          .sidebar .nav-link {
            color: #212529;
          }
          .sidebar .nav-link:hover {
            color: #007bff;
          }
          .selectgroup {
            display: flex;
            gap: 10px;
          }
          .selectgroup-item {
            display: flex;
            align-items: center;
          }
          .selectgroup-button {
            padding: 5px 10px;
            border: 1px solid #ced4da;
            border-radius: 4px;
            cursor: pointer;
          }
          .selectgroup-input:checked + .selectgroup-button {
            background-color: #007bff;
            color: #fff;
            border-color: #007bff;
          }
        `}
      </style>

      <div className="wrapper">
        <Nav />
        <div className="main-panel bg-light">
          <div className="content bg-light">
            <div className="page-inner">
              <div className="row profile">
                <div className="p-2 col-md-12">
                  <div className="card p-md-5 p-1 shadow-lg bg-light">
                    <nav>
                      <ul className="nav nav-tabs nav-fill" id="nav-tab" role="tablist">
                        <li className="nav-item">
                          <a
                            className={`nav-link ${activeTab === "per" ? "active" : ""}`}
                            id="nav-per-tab"
                            onClick={() => handleTabChange("per")}
                            role="tab"
                            aria-controls="per"
                            aria-selected={activeTab === "per"}
                          >
                            Personal Settings
                          </a>
                        </li>
                        <li className="nav-item">
                          <a
                            className={`nav-link ${activeTab === "set" ? "active" : ""}`}
                            id="nav-set-tab"
                            onClick={() => handleTabChange("set")}
                            role="tab"
                            aria-controls="set"
                            aria-selected={activeTab === "set"}
                          >
                            Withdrawal Settings
                          </a>
                        </li>
                        <li className="nav-item">
                          <a
                            className={`nav-link ${activeTab === "pas" ? "active" : ""}`}
                            id="nav-pas-tab"
                            onClick={() => handleTabChange("pas")}
                            role="tab"
                            aria-controls="pas"
                            aria-selected={activeTab === "pas"}
                          >
                            Password/Security
                          </a>
                        </li>
                        <li className="nav-item">
                          <a
                            className={`nav-link ${activeTab === "sec" ? "active" : ""}`}
                            id="nav-sec-tab"
                            onClick={() => handleTabChange("sec")}
                            role="tab"
                            aria-controls="sec"
                            aria-selected={activeTab === "sec"}
                          >
                            Other Settings
                          </a>
                        </li>
                      </ul>
                    </nav>
                    <div className="tab-content">
                      <div className={`tab-pane fade ${activeTab === "per" ? "show active" : ""}`} id="per" role="tabpanel">
                        <form onSubmit={handleProfileSubmit}>
                          <div className="form-row">
                            <div className="form-group col-md-6">
                              <h5 className="text-dark">Fullname</h5>
                              <input
                                type="text"
                                className="form-control bg-light text-dark"
                                name="name"
                                value={profileForm.name}
                                onChange={handleProfileChange}
                                placeholder="Enter your name"
                                required
                                aria-label="Full Name"
                              />
                            </div>
                            <div className="form-group col-md-6">
                              <h5 className="text-dark">Email Address</h5>
                              <input
                                type="email"
                                className="form-control bg-light text-dark"
                                name="email"
                                value={profileForm.email}
                                readOnly
                                aria-label="Email Address"
                              />
                            </div>
                            <div className="form-group col-md-6">
                              <h5 className="text-dark">Phone Number</h5>
                              <input
                                type="text"
                                className="form-control bg-light text-dark"
                                name="phone"
                                value={profileForm.phone}
                                onChange={handleProfileChange}
                                placeholder="Enter phone number"
                                aria-label="Phone Number"
                              />
                            </div>
                            <div className="form-group col-md-6">
                              <h5 className="text-dark">Date of Birth</h5>
                              <input
                                type="date"
                                className="form-control bg-light text-dark"
                                name="dob"
                                value={profileForm.dob}
                                onChange={handleProfileChange}
                                aria-label="Date of Birth"
                              />
                            </div>
                            <div className="form-group col-md-6">
                              <h5 className="text-dark">Address</h5>
                              <textarea
                                className="form-control bg-light text-dark"
                                name="address"
                                value={profileForm.address}
                                onChange={handleProfileChange}
                                placeholder="Enter full address"
                                rows={3}
                                aria-label="Address"
                              />
                            </div>
                          </div>
                          {message && (
                            <div className={`message ${message.includes("success") ? "success" : "error"}`}>
                              {message}
                            </div>
                          )}
                          <button type="submit" className="btn btn-primary" disabled={submitting}>
                            {submitting ? "Updating..." : "Update Profile"}
                          </button>
                        </form>
                      </div>
                      <div className={`tab-pane fade ${activeTab === "set" ? "show active" : ""}`} id="set" role="tabpanel">
                        <form onSubmit={handleWithdrawalSubmit}>
                          <fieldset className="mt-2">
                            <legend>Coin Address</legend>
                            <div className="form-row">
                              <div className="form-group col-md-6">
                                <h5 className="text-dark">Bitcoin</h5>
                                <input
                                  type="text"
                                  name="btc_address"
                                  value={withdrawalForm.btc_address}
                                  onChange={handleWithdrawalChange}
                                  className="form-control text-dark bg-light"
                                  placeholder="Enter Bitcoin Address"
                                  aria-label="Bitcoin Address"
                                />
                                <small className="text-dark">
                                  Enter your Bitcoin Address for withdrawals
                                </small>
                              </div>
                              <div className="form-group col-md-6">
                                <h5 className="text-dark">Ethereum</h5>
                                <input
                                  type="text"
                                  name="eth_address"
                                  value={withdrawalForm.eth_address}
                                  onChange={handleWithdrawalChange}
                                  className="form-control text-dark bg-light"
                                  placeholder="Enter Ethereum Address"
                                  aria-label="Ethereum Address"
                                />
                                <small className="text-dark">
                                  Enter your Ethereum Address for withdrawals
                                </small>
                              </div>
                              <div className="form-group col-md-6">
                                <h5 className="text-dark">Litecoin</h5>
                                <input
                                  type="text"
                                  name="ltc_address"
                                  value={withdrawalForm.ltc_address}
                                  onChange={handleWithdrawalChange}
                                  className="form-control text-dark bg-light"
                                  placeholder="Enter Litecoin Address"
                                  aria-label="Litecoin Address"
                                />
                                <small className="text-dark">
                                  Enter your Litecoin Address for withdrawals
                                </small>
                              </div>
                              <div className="form-group col-md-6">
                                <h5 className="text-dark">USDT</h5>
                                <input
                                  type="text"
                                  name="usdt_address"
                                  value={withdrawalForm.usdt_address}
                                  onChange={handleWithdrawalChange}
                                  className="form-control text-dark bg-light"
                                  placeholder="Enter USDT Address"
                                  aria-label="USDT Address"
                                />
                                <small className="text-dark">
                                  Enter your USDT Address for withdrawals
                                </small>
                              </div>
                              <div className="form-group col-md-6">
                                <h5 className="text-dark">Tron</h5>
                                <input
                                  type="text"
                                  name="tron_address"
                                  value={withdrawalForm.tron_address}
                                  onChange={handleWithdrawalChange}
                                  className="form-control text-dark bg-light"
                                  placeholder="Enter Tron Address"
                                  aria-label="Tron Address"
                                />
                                <small className="text-dark">
                                  Enter your Tron Address for withdrawals
                                </small>
                              </div>
                            </div>
                          </fieldset>
                          {message && (
                            <div className={`message ${message.includes("success") ? "success" : "error"}`}>
                              {message}
                            </div>
                          )}
                          <button type="submit" className="px-5 btn btn-primary" disabled={submitting}>
                            {submitting ? "Saving..." : "Save"}
                          </button>
                        </form>
                      </div>
                      <div className={`tab-pane fade ${activeTab === "pas" ? "show active" : ""}`} id="pas" role="tabpanel">
                        <form onSubmit={handlePasswordSubmit}>
                          <div className="form-row">
                            <div className="form-group col-md-6">
                              <h5 className="text-dark">Old Password</h5>
                              <input
                                type="password"
                                name="current_password"
                                value={passwordForm.current_password}
                                onChange={handlePasswordChange}
                                className="form-control text-dark bg-light"
                                required
                                aria-label="Current Password"
                              />
                            </div>
                            <div className="form-group col-md-6">
                              <h5 className="text-dark">New Password</h5>
                              <input
                                type="password"
                                name="password"
                                value={passwordForm.password}
                                onChange={handlePasswordChange}
                                className="form-control text-dark bg-light"
                                required
                                aria-label="New Password"
                              />
                            </div>
                            <div className="form-group col-md-6">
                              <h5 className="text-dark">Confirm New Password</h5>
                              <input
                                type="password"
                                name="password_confirmation"
                                value={passwordForm.password_confirmation}
                                onChange={handlePasswordChange}
                                className="form-control text-dark bg-light"
                                required
                                aria-label="Confirm New Password"
                              />
                            </div>
                          </div>
                          {message && (
                            <div className={`message ${message.includes("success") ? "success" : "error"}`}>
                              {message}
                            </div>
                          )}
                          <button type="submit" className="btn btn-primary" disabled={submitting}>
                            {submitting ? "Updating..." : "Update Password"}
                          </button>
                        </form>
                        <div className="mt-4">
                          <a
                            href="/dashboards/manage-account-security"
                            className="text-decoration-none"
                          >
                            Advance Account Settings <i className="fas fa-arrow-right" />
                          </a>
                        </div>
                      </div>
                      <div className={`tab-pane fade ${activeTab === "sec" ? "show active" : ""}`} id="sec" role="tabpanel">
                        <form onSubmit={handleEmailPrefSubmit}>
                          <div className="row">
                            <div className="mb-3 col-md-6">
                              <h5 className="text-dark">Send confirmation OTP to my email when withdrawing funds</h5>
                              <div className="selectgroup">
                                <label className="selectgroup-item">
                                  <input
                                    type="radio"
                                    name="otpsend"
                                    value="Yes"
                                    checked={emailPrefForm.otpsend === "Yes"}
                                    onChange={handleEmailPrefChange}
                                    className="selectgroup-input"
                                  />
                                  <span className="selectgroup-button">Yes</span>
                                </label>
                                <label className="selectgroup-item">
                                  <input
                                    type="radio"
                                    name="otpsend"
                                    value="No"
                                    checked={emailPrefForm.otpsend === "No"}
                                    onChange={handleEmailPrefChange}
                                    className="selectgroup-input"
                                  />
                                  <span className="selectgroup-button">No</span>
                                </label>
                              </div>
                            </div>
                            <div className="mb-3 col-md-6">
                              <h5 className="text-dark">Send me email when I get profit</h5>
                              <div className="selectgroup">
                                <label className="selectgroup-item">
                                  <input
                                    type="radio"
                                    name="roiemail"
                                    value="Yes"
                                    checked={emailPrefForm.roiemail === "Yes"}
                                    onChange={handleEmailPrefChange}
                                    className="selectgroup-input"
                                  />
                                  <span className="selectgroup-button">Yes</span>
                                </label>
                                <label className="selectgroup-item">
                                  <input
                                    type="radio"
                                    name="roiemail"
                                    value="No"
                                    checked={emailPrefForm.roiemail === "No"}
                                    onChange={handleEmailPrefChange}
                                    className="selectgroup-input"
                                  />
                                  <span className="selectgroup-button">No</span>
                                </label>
                              </div>
                            </div>
                            <div className="mb-3 col-md-6">
                              <h5 className="text-dark">Send me email when my investment plan expires</h5>
                              <div className="selectgroup">
                                <label className="selectgroup-item">
                                  <input
                                    type="radio"
                                    name="invplanemail"
                                    value="Yes"
                                    checked={emailPrefForm.invplanemail === "Yes"}
                                    onChange={handleEmailPrefChange}
                                    className="selectgroup-input"
                                  />
                                  <span className="selectgroup-button">Yes</span>
                                </label>
                                <label className="selectgroup-item">
                                  <input
                                    type="radio"
                                    name="invplanemail"
                                    value="No"
                                    checked={emailPrefForm.invplanemail === "No"}
                                    onChange={handleEmailPrefChange}
                                    className="selectgroup-input"
                                  />
                                  <span className="selectgroup-button">No</span>
                                </label>
                              </div>
                            </div>
                          </div>
                          {message && (
                            <div className={`message ${message.includes("success") ? "success" : "error"}`}>
                              {message}
                            </div>
                          )}
                          <button type="submit" className="px-5 btn btn-primary" disabled={submitting}>
                            {submitting ? "Saving..." : "Save"}
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
<Footer/>
            <div
              style={{
                position: "fixed",
                left: 0,
                bottom: 0,
                zIndex: 1,
                width: "100%",
              }}
            >
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#FFFFFF",
                  overflow: "hidden",
                  boxSizing: "content-box",
                  textAlign: "right",
                  lineHeight: "14px",
                  fontSize: "12px",
                  fontFeatureSettings: "normal",
                  textSizeAdjust: "100%",
                  padding: "0px",
                  margin: "0px",
                }}
              >
                <div style={{ height: "40px" }}>
                  <iframe
                    src="https://widget.coinlib.io/widget?type=horizontal_v2&theme=light&pref_coin_id=1505&invert_hover="
                    width="100%"
                    height={36}
                    scrolling="auto"
                    marginWidth={0}
                    marginHeight={0}
                    frameBorder={0}
                    border={0}
                    style={{ border: 0, margin: 0, padding: 0 }}
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
